import { AxiosResponse } from 'axios';
import { ZodType } from 'zod';
import i18n from '~app/i18n';
import { ApiErrorData, ApiErrorDataDto } from './api.types';

/**
 * Creates a middleware that parses the data field of an Axios response
 * using the provided zod schema.
 *
 * @param schema - The zod schema to use for parsing the data field.
 * @returns A middleware that takes an Axios response and returns a new
 * Axios response with the parsed data field.
 */
export const responseContract = <Data>(schema: ZodType<Data>) => {
  return (response: AxiosResponse<unknown>): AxiosResponse<Data> => {
    const data = schema.parse(response.data);

    return { ...response, data };
  };
};

/**
 * Normalize API errors into a flat list of user-facing strings (one per error).
 *
 * Resolution order per error:
 *   1. a localized message keyed by the backend error code (`errors:api.<code>`),
 *      so codes like `no_contingents` render in the guest's language;
 *   2. the backend-provided `detail` (for codes without a frontend translation);
 *   3. the raw error code as a last resort.
 *
 * Add a new key under `api` in `app/locales/<lng>/errors.json` to localize an
 * additional backend error code — no code change needed here.
 */
export const normalizeValidationErrors = (
  data: ApiErrorDataDto,
): ApiErrorData => {
  return data.errors.map(({ error, detail }) => {
    if (error) {
      const key = `api.${error}`;
      if (i18n.exists(key, { ns: 'errors' })) {
        return i18n.t(key, { ns: 'errors' });
      }
    }
    return detail ?? error ?? '';
  });
};
